import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, ReplaySubject, share, switchMap, tap } from 'rxjs';
import { SpacesService } from 'account-hybrid/common/components/spaces';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { SpacetimeCatalog, SpacetimeInsightStatus } from 'account-hybrid/features/spacetime/models/spacetime-catalog.model';

interface CatalogEntry {
    /** The retained catalog of one space; emits again after every write and never errors. */
    live: ReplaySubject<SpacetimeCatalog>;
    loaded: boolean;
    /** The read in flight, shared by everyone asking while it runs. */
    read$: Observable<SpacetimeCatalog> | null;
}

// The Spacetime insights catalog of the current space, retained for the session. Every write answers with
// the whole catalog, which replaces the retained one so the nav, the catalog page and the explainer re-render.
@Injectable({ providedIn: 'root' })
export class SpacetimeCatalogService {
    private readonly http = inject(HttpClient);
    private readonly spacesService = inject(SpacesService);
    private readonly environment = inject<Environment>(ENVIRONMENT_TOKEN);
    private readonly entries = new Map<string, CatalogEntry>();

    /** The current space's catalog; emits again after each change made through this service. A failed read errors. */
    getCatalog$(): Observable<SpacetimeCatalog> {
        const url = this.url();
        const entry = this.entry(url);
        if (entry.loaded) return entry.live.asObservable();
        if (!entry.read$) {
            entry.read$ = this.http.get<SpacetimeCatalog>(url).pipe(
                tap({
                    next: catalog => this.publish(entry, catalog),
                    // A failed read is forgotten, so the next ask retries instead of locking the space out for the session.
                    finalize: () => { entry.read$ = null; },
                }),
                share(),
            );
        }
        return entry.read$.pipe(switchMap(() => entry.live));
    }

    /** The current space's catalog once any read or write has loaded it; never errors, so a view whose read failed can still follow a later success. */
    live$(): Observable<SpacetimeCatalog> {
        return this.entry(this.url()).live.asObservable();
    }

    /** Ordered pinned insight keys for this user; an empty list resets to the space default. */
    savePins$(pins: string[]): Observable<SpacetimeCatalog> {
        return this.change$(this.http.put<SpacetimeCatalog>(this.url('/pins'), pins));
    }

    /** Records that the user opened the insight or, with `explainer`, its explainer, which clears its NEW mark. */
    markViewed$(insightKey: string, explainer: boolean): Observable<SpacetimeCatalog> {
        return this.change$(this.http.post<SpacetimeCatalog>(this.url(`/insights/${encodeURIComponent(insightKey)}/viewed`), { Explainer: explainer }));
    }

    /** Flip.to staff only: makes these pins the space default, which Reset pins and new users fall back to. */
    saveDefaultPins$(pins: string[]): Observable<SpacetimeCatalog> {
        return this.change$(this.http.put<SpacetimeCatalog>(this.url('/default-pins'), pins));
    }

    /** Flip.to staff only: sets the insight's status for this space. */
    setStatus$(insightKey: string, status: SpacetimeInsightStatus): Observable<SpacetimeCatalog> {
        return this.change$(this.http.put<SpacetimeCatalog>(this.url(`/insights/${encodeURIComponent(insightKey)}/status`), { Status: status }));
    }

    /** Flip.to staff only: copies another space's insight statuses onto this one. */
    applyTemplate$(sourceContextUuid: string, sourceCompanyUuid: string | null = null): Observable<SpacetimeCatalog> {
        return this.change$(this.http.post<SpacetimeCatalog>(this.url('/apply-template'), {
            SourceContextUuid: sourceContextUuid,
            SourceCompanyUuid: sourceCompanyUuid,
        }));
    }

    private change$(request$: Observable<SpacetimeCatalog>): Observable<SpacetimeCatalog> {
        const entry = this.entry(this.url());
        return request$.pipe(tap(catalog => this.publish(entry, catalog)));
    }

    // A missing array (version skew, an error page served as 200) must not throw inside every consumer's computed.
    private publish(entry: CatalogEntry, catalog: SpacetimeCatalog): void {
        entry.loaded = true;
        entry.live.next({
            Insights: catalog?.Insights ?? [],
            Pins: catalog?.Pins ?? [],
            RecentlyViewed: catalog?.RecentlyViewed ?? [],
        });
    }

    private entry(url: string): CatalogEntry {
        let entry = this.entries.get(url);
        if (!entry) {
            entry = { live: new ReplaySubject<SpacetimeCatalog>(1), loaded: false, read$: null };
            this.entries.set(url, entry);
        }
        return entry;
    }

    // A Mixed space reads the property's catalog with the company as fallback: the server answers from the
    // property's own statuses and, while it has none, the company's.
    private url(path = ''): string {
        const base = this.environment.apiBaseUrl;
        if (this.spacesService.isMixedSpace()) {
            const companyUuid = this.spacesService.getCompanyUuid();
            return `${base}/${this.spacesService.getPropertyUuid()}/spacetime/catalog${path}?companyUuid=${encodeURIComponent(companyUuid ?? '')}`;
        }
        const contextUuid = this.spacesService.hasCompanyContext()
            ? this.spacesService.getCompanyUuid()
            : this.spacesService.getPropertyUuid();
        return `${base}/${contextUuid}/spacetime/catalog${path}`;
    }
}
